// eslint-disable-next-line import/no-webpack-loader-syntax
import "!style-loader!css-loader!react-big-calendar/lib/css/react-big-calendar.css";
import { toastDBCreateError } from '@common/utils/db-create-error-toast';
// eslint-disable-next-line import/no-webpack-loader-syntax
import "!style-loader!css-loader!react-big-calendar/lib/addons/dragAndDrop/styles.css";
// eslint-disable-next-line import/no-webpack-loader-syntax
import "!style-loader!css-loader!./calendar-custom.css";
// Note: These CSS files are also imported in css-modules-entry.js for shadow DOM injection
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import BTThemeProvider from "@common-components/bt-theme-provider";
import { useAppSelector, useAppDispatch } from "@common-reducers/hooks/store.hook";
import { MessageConfirmationDialog } from "@extension/context/content/components/add-scheduled-message-button/message-confirmation-dialog";
import CampaignRowMenu from "@extension/context/content/components/bt-bulk-dashboard/campaign-row-menu";
import { Box, DialogContent } from "@mui/material";
import {
    DropdownMenu,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import MessageRowMenu from "@extension/context/content/components/bt-bulk-dashboard/message-row-menu";
import moment from "moment";
import { Suspense, SyntheticEvent, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";
// react-big-calendar is now TYPE-ONLY here — its runtime (Calendar,
// momentLocalizer, withDragAndDrop) is code-split into the lazy/calendar.js WAR
// bundle and consumed via <LazyDnDCalendar> below. Keeping these as `import type`
// guarantees webpack never pulls the lib back into app.js through this module.
import type {
  EventProps,
  NavigateAction,
  stringOrDate,
  ToolbarProps,
  View
} from "react-big-calendar";
import LazyDnDCalendar, { CalendarSkeleton } from "./lazy-dnd-calendar";
import { computeDroppedDueDate } from "./drop-datetime";
import { CustomDayBackground } from "./custom-day-background";
import { CustomMonthEventWrapper } from './custom-message-event-wrapper';
import CustomToolbar from "./custom-toolbar";
// Removed MessageColor-related logic as the dialog is not used
// import { isTimeToUpdateContacts } from "@common-reducers/WhatsAppSelectors";
// ScheduledMessageEditorPopup is now injected separately via scheduled-message-dialog.injection.tsx
// No need to import it here - it's handled by the separate shadow DOM injection
// import ScheduledMessageEditorPopup from "@/extension/context/content/components/add-scheduled-message-button/scheduled-message-editor-popup";
import { editCalendarEventThunk, handleNewMessageDialogThunk, handleRecurringEventDragThunk, onRecurringDialogCloseThunk, scheduleMessageFromLeanEventThunk } from "@common-reducers/CalendarThunks";
import { onCampaignEventDropThunk, getPendingAndRuningCampaigns } from "@common-reducers/CampaignThunks";
import { getCalendarLeanEvents } from "@common-reducers/WhatsAppSelectors";
import { DBBoardsThunks, AUDIENCE_LIST_QUERY } from "@common-reducers/DBServiceThunks";
import { setCurrentDate, setView } from "@common/reducers/CalendarReducer";
// BE#313 — edit a follow-up occurrence's own drip step.
import { useNavigate } from "react-router-dom";
import { isExtensionContext } from "@common/services/detect-context";
import { campaignPagePath, navigateToCampaignPage } from "@common-views/campaigns-redesign/open-campaign-page";
import { resolveCalendarEditTarget } from "./calendar-edit-target";
import { getUserStartDayOfWeek } from "@common/reducers/UserSelectors";
import { getPendingUserMessages } from "@common/thunks/UserMessagesThunks";
import { loadSentHistoryForRange } from "@common-reducers/planner-sent-history";
import { normalizePlannerFilters } from "@common/services/planner-filters";
import { getCalendarVisibleRange, CalendarViewName } from "@common/views/calendar/calendar-range";
import { CommonRootState } from "@common/types/common-root-state-type";
import { CustomDayCell } from "./custom-day-cell";
import CustomMonthDayBackground from "./custom-month-day-background";
import NewMessageFab from "@common/components/new-message-fab/new-message-fab";
import { isRtlLanguage } from "@common-services/rtl";
import { applyMomentLocale } from "@common-config/moment-locales";
import { useIsMobile } from "@common/hooks/use-is-mobile";




// DnDCalendar (withDragAndDrop(Calendar)) is now composed inside the lazy
// component bundle — see ./lazy-dnd-calendar.tsx.

export interface CalendarEvent extends UserMessage {
  dropDate?: moment.Moment;
}


export type EventPL = {
  isCampaign: boolean;
  _id: string;
  dueDate: string;
  isRecurring: boolean;
} & Partial<DOMRect>;

// Moved to their own module so consumers can use them without importing this
// view (which pulls in react-big-calendar's stylesheets). Re-exported here so
// existing importers keep working.
export type { confirmationDialogOption } from "./recurring-dialog-options";
export { confirmationDialogOptions } from "./recurring-dialog-options";
import { confirmationDialogOptions as confirmationDialogOptionsLocal } from "./recurring-dialog-options";
import type { confirmationDialogOption } from "./recurring-dialog-options";



// DEBUG: Performance tracking - disabled by default for production
const DEBUG_PERF = false; // Set to true to enable performance logging
const PERF_PREFIX = '[CALENDAR_PERF]';
let renderCount = 0;

// Track initial load
if (DEBUG_PERF && typeof window !== 'undefined' && !(window as any).__calendarPerfInit) {
  (window as any).__calendarPerfInit = performance.now();
  console.log(`${PERF_PREFIX} Calendar module loaded at: ${(window as any).__calendarPerfInit.toFixed(2)}ms`);
}

// OPTIMIZATION: the moment localizer is now created once inside the lazy bundle
// (./lazy-dnd-calendar.tsx) at load time — same "build once" guarantee, off the
// app.js critical path. It binds the shared `moment`, so runtime
// moment.updateLocale(...) calls below still reflect in the calendar.

// OPTIMIZATION: Stable module-level accessors/formats/style so the Calendar's props keep
// referential identity across renders (prevents needless re-renders inside react-big-calendar)
const calendarTitleAccessor = (event: any) => event.isCampaign ? event.name : event.message;
const calendarEventDateAccessor = (event: any) => new Date(event.dueDate);
const calendarDraggableAccessor = () => true;

const calendarFormats = {
  eventTimeRangeFormat: () => '',
  timeGutterFormat: (date: Date, culture?: string, localizer?: any) =>
    localizer.format(date, 'HH:mm', culture)
};

const calendarStyle = {
  height: '100%',
  backgroundColor: 'var(--calendar-bg)',
};

// Stable time bounds — computed once at module load so their identity doesn't
// change on every render (inline moment()...toDate() would defeat the Calendar's
// memoization). These pin the time-of-day window; the date portion is irrelevant
// to react-big-calendar's scroll/min/max.
const calendarScrollToTime = moment().startOf('day').toDate();
const calendarMinTime = moment().startOf('day').toDate();
const calendarMaxTime = moment().endOf('day').toDate();

// OPTIMIZATION: containerWidth is delivered via context so the `components` map identity does
// not depend on it — a small resize re-renders event chips only, without remounting day cells
const CalendarContainerWidthContext = createContext<number>(0);

const CalendarMonthEvent = (v: EventProps<any>) => {
  const containerWidth = useContext(CalendarContainerWidthContext);
  return (
    <CustomMonthEventWrapper
      eventProps={v}
      containerWidth={containerWidth}
    />
  );
};

const CalendarDateHeader = ({ date, label }: { date: Date; label: string }) => (
  <CustomDayCell date={date} label={label} />
);

const CalendarDateCellWrapper = ({ value }: { value: Date }) => (
  <CustomMonthDayBackground date={value} />
);

const CalendarTimeSlotWrapper = ({ children, value }: { children?: React.ReactNode; value: Date }) => (
  <CustomDayBackground date={value}>{children}</CustomDayBackground>
);

// react-router's useNavigate() throws ("useNavigate() may be used only in the
// context of a <Router> component") when there is no <Router> above it. The
// CALENDAR is shared: the website renders it inside its history Router (real
// URLs), but the EXTENSION injects it as a shadow-DOM overlay on web.whatsapp.com
// with NO react-router Router (campaign routing there is the campaignsMemoryRouter
// singleton — see campaigns-routes.tsx). Calling the hook unconditionally crashed
// the whole calendar in the extension (calendar wouldn't open). This bridge
// isolates the website-only hook: it is rendered ONLY when !isExtensionContext(),
// so the hook never runs under the overlay, and it publishes the browser router's
// navigate into a ref the click handler reads. (BE#313 followup.)
function WebsiteNavigateBridge({
  navigateRef,
}: {
  navigateRef: React.MutableRefObject<((path: string) => void) | null>;
}) {
  const navigate = useNavigate();
  navigateRef.current = navigate;
  return null;
}

function CalendarView() {
  renderCount++;
  const renderStart = DEBUG_PERF ? performance.now() : 0;
  if (DEBUG_PERF) console.log(`${PERF_PREFIX} ========== RENDER #${renderCount} START ==========`);

  const { i18n } = useTranslation(namespace);
  const isRtl = isRtlLanguage(i18n.language);
  const isMobile = useIsMobile();
  // localizer is now created once outside the component
  const [dndEvent, setDndEvent] = useState<EventPL | null>(null);
  const [showDndConfirmationDialog, setShowDndConfirmationDialog] =
    useState<boolean>(false);
  const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  // BE#313 — a drip occurrence opens the unified SequenceDetail (/campaigns/:id, ALL steps), the same
  // route the campaigns list uses, so editing from the calendar shows every step (not just the first).
  // NOTE: we do NOT call useNavigate() here — it throws in the extension overlay
  // (no <Router>). Website navigation comes through WebsiteNavigateBridge → this
  // ref; the extension uses campaignsMemoryRouter + openBulkDashboard (see below).
  const websiteNavigateRef = useRef<((path: string) => void) | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const menuJustClosedRef = useRef<boolean>(false);

  // Message menu state - משתנים לניהול התפריט של הודעות רגילות
  const [selectedMessageEvent, setSelectedMessageEvent] = useState<any | null>(null);
  const [isMessageMenuOpen, setIsMessageMenuOpen] = useState(false);
  const [messageMenuPosition, setMessageMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const messageMenuTriggerRef = useRef<HTMLButtonElement>(null);

  // Track pending setTimeout ids so they can be cleared on unmount
  const pendingTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // DEBUG: Track selector performance
  const selectorStart = DEBUG_PERF ? performance.now() : 0;

  // Remove loading state - rely on Redux
  const currentDate = useAppSelector((state) => state.CalendarReducer.currentDate);
  if (DEBUG_PERF) console.log(`${PERF_PREFIX} currentDate selector: ${(performance.now() - selectorStart).toFixed(2)}ms`);

  const eventsStart = DEBUG_PERF ? performance.now() : 0;
  const rawEvents = useAppSelector(state => getCalendarLeanEvents(state));
  if (DEBUG_PERF) console.log(`${PERF_PREFIX} events selector: ${(performance.now() - eventsStart).toFixed(2)}ms, count: ${rawEvents?.length}`);

  // DEBUG: Set to true to test calendar without any events
  const DEBUG_DISABLE_EVENTS = false;
  const events = DEBUG_DISABLE_EVENTS ? [] : rawEvents;
  if (DEBUG_PERF && DEBUG_DISABLE_EVENTS) console.log(`${PERF_PREFIX} DEBUG: Events disabled for testing`);

  // ScheduledMessageEditorPopup is now handled by separate injection, no need to track open state here
  // const isAddScheduledMessageModalOpen = useAppSelector((state) => state.AddScheduledMessageButtonReducer.open);
  const view = useAppSelector((state) => state.CalendarReducer.view);
  const startDayAtWeek = useAppSelector(getUserStartDayOfWeek);
  const focusedDate = useAppSelector((state: CommonRootState) => state.MainReducer.viewMonthStart);


  if (DEBUG_PERF) console.log(`${PERF_PREFIX} All selectors done: ${(performance.now() - selectorStart).toFixed(2)}ms`);

  // DEBUG: Track what changed between renders (only when DEBUG_PERF is enabled)
  const prevStateRef = useRef<any>({});
  useEffect(() => {
    if (!DEBUG_PERF) return;

    const changes: string[] = [];
    if (prevStateRef.current.currentDate !== currentDate) changes.push('currentDate');
    if (prevStateRef.current.events !== events) changes.push(`events(${events?.length})`);
    if (prevStateRef.current.view !== view) changes.push('view');
    if (prevStateRef.current.startDayAtWeek !== startDayAtWeek) changes.push('startDayAtWeek');
    if (prevStateRef.current.focusedDate !== focusedDate) changes.push('focusedDate');
    if (prevStateRef.current.containerWidth !== containerWidth) changes.push('containerWidth');

    if (changes.length > 0) {
      console.log(`${PERF_PREFIX} State changes triggered render: ${changes.join(', ')}`);
    }

    prevStateRef.current = { currentDate, events, view, startDayAtWeek, focusedDate, containerWidth };
  });

  const dispatch = useAppDispatch();

  // Set moment locale based on i18n language so react-big-calendar
  // renders month / day names in the correct language.
  useEffect(() => {
    applyMomentLocale(i18n.resolvedLanguage || i18n.language || 'en');
  }, [i18n.language, i18n.resolvedLanguage]);

  // Dispatch action to load messages when component mounts
  useEffect(() => {
    dispatch(getPendingUserMessages());
    // BE#313 — the web `/calendar` route renders <CalendarView/> directly, so a direct nav / refresh
    // must load the campaigns HERE (bootstrap only fires via other containers, e.g. whatsapp-view).
    // getPendingAndRuningCampaigns loads the drip-step campaigns AND (BE#313) their drip docs, so the
    // calendar can spread every follow-up step regardless of which path reached it.
    dispatch(getPendingAndRuningCampaigns());
    // FE#332 — load the audience boards so getCalendarLeanEvents can resolve a
    // campaign/drip's boardIDArray to the target GROUP NAMES for the hover tooltip.
    // Same query AudienceList uses (high $limit so all audiences load).
    dispatch(DBBoardsThunks.find({ type: 'audience', deleted: { $ne: true }, ...AUDIENCE_LIST_QUERY }));
  }, [dispatch]);

  // What already went out loads ONLY while "Show sent" is on, only for the visible range and
  // the kinds that are on, never twice for a covered range (see planner-sent-history.ts).
  const plannerFiltersForFetch = useAppSelector((state) => state.CalendarReducer.calendarSidebarFilters);
  const showSentForFetch = normalizePlannerFilters(plannerFiltersForFetch).showSent;
  useEffect(() => {
    if (!showSentForFetch) return;
    const { start, end } = getCalendarVisibleRange(currentDate, view as CalendarViewName);
    dispatch(loadSentHistoryForRange({ start, end }));
  }, [dispatch, showSentForFetch, plannerFiltersForFetch, currentDate, view]);

  // Create a ref for the container div
  const calendarContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Event handlers - currently just placeholders, blocking is handled at DialogOverlay level
  const handleClick = useCallback((_e: Event) => {
    // No-op - blocking is handled at DialogOverlay level
  }, []);

  const handleMouseDown = useCallback((_e: Event) => {
    // No-op - blocking is handled at DialogOverlay level
  }, []);

  // Ref callback to attach event listeners directly to the container
  const setCalendarContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (calendarContainerRef.current && node !== calendarContainerRef.current) {
      // Cleanup old listeners if ref changes
      const oldContainer = calendarContainerRef.current;
      const oldRootNode = oldContainer.getRootNode();
      const oldShadowRoot = oldRootNode instanceof ShadowRoot ? oldRootNode : null;
      const oldListenerTarget = oldShadowRoot || oldContainer;
      
      oldListenerTarget.removeEventListener('click', handleClick, true);
      oldListenerTarget.removeEventListener('mousedown', handleMouseDown, true);
    }

    calendarContainerRef.current = node;

    if (node) {
      // Get the shadow root - this is the highest level we can attach to
      // Events from both calendar and dialog portal will pass through here
      const rootNode = node.getRootNode();
      const shadowRoot = rootNode instanceof ShadowRoot ? rootNode : null;
      const listenerTarget = shadowRoot || node;

      // Attach listeners at shadow root level (if available) to catch events from all children
      // This ensures we catch dialog backdrop clicks before they reach react-big-calendar
      listenerTarget.addEventListener('click', handleClick, true); // Use capture phase
      listenerTarget.addEventListener('mousedown', handleMouseDown, true); // Use capture phase for mousedown too
    }
  }, [handleClick, handleMouseDown]);

  // Simplified resize handling: remove overlay and debounce logic

  // Simplified width update - no throttling needed since events are hidden during resize
  const updateContainerWidth = useCallback((width: number) => {
    setContainerWidth(prevWidth => {
      if (Math.abs(prevWidth - width) > 0.5) {
        return width;
      }
      return prevWidth;
    });
  }, []);

  // No special cleanup required

  // Shadow DOM drag & drop fix: Make react-big-calendar work with Shadow DOM
  // by patching document.elementFromPoint to look inside shadow roots
  useEffect(() => {
    if (!calendarContainerRef.current) return;

    const shadowRoot = calendarContainerRef.current.getRootNode();
    if (!(shadowRoot instanceof ShadowRoot)) return;

    console.log('[meilook Calendar DnD] Setting up Shadow DOM compatibility');

    // Store original functions
    const originalElementFromPoint = document.elementFromPoint.bind(document);
    const originalElementsFromPoint = document.elementsFromPoint.bind(document);

    // Patch elementFromPoint to look inside shadow roots
    document.elementFromPoint = function(x: number, y: number): Element | null {
      const element = originalElementFromPoint(x, y);
      
      // If we hit the shadow host, look inside the shadow root
      if (element && element.shadowRoot) {
        const innerElement = element.shadowRoot.elementFromPoint(x, y);
        if (innerElement) {
          return innerElement;
        }
      }
      
      return element;
    };

    // Patch elementsFromPoint to include shadow root elements
    document.elementsFromPoint = function(x: number, y: number): Element[] {
      const elements = originalElementsFromPoint(x, y);
      const result: Element[] = [];
      
      for (const element of elements) {
        result.push(element);
        
        // If element has shadow root, add elements from inside it
        if (element.shadowRoot) {
          const innerElements = element.shadowRoot.elementsFromPoint(x, y);
          result.push(...innerElements);
        }
      }
      
      return result;
    };

    console.log('[meilook Calendar DnD] Shadow DOM compatibility enabled');

    // ALSO forward drag events from shadow root to document
    // This is needed because react-big-calendar listens on document
    const dragEvents = ['dragstart', 'drag', 'dragenter', 'dragover', 'dragleave', 'drop', 'dragend'];
    const dragForwardListeners: Array<() => void> = [];

    // Forward mouse events (needed for week/day drag which relies on Selection API)
    const mouseEvents = ['mousedown', 'mousemove', 'mouseup'];
    const mouseForwardListeners: Array<() => void> = [];

    dragEvents.forEach(eventType => {
      const forwarder = ((e: DragEvent) => {
        // Prevent default for dragover and drop
        if (eventType === 'dragover' || eventType === 'drop') {
          e.preventDefault();
        }

        // Store the original event
        const storedEvent = e;
        
        // Temporarily make document.elementFromPoint return elements from shadow
        // This is critical for react-big-calendar to find the drop target
        const tempPatch = document.elementFromPoint;
        document.elementFromPoint = function(x: number, y: number): Element | null {
          const fromShadow = shadowRoot.elementFromPoint(x, y);
          return fromShadow || tempPatch.call(document, x, y);
        };

        // Dispatch the event to document
        // We can't create a new DragEvent with dataTransfer, so we'll trigger
        // document listeners directly by dispatching a generic event
        try {
          // Call any listeners on document for this event type
          const listeners = (document as any).__drag_listeners || {};
          if (listeners[eventType]) {
            listeners[eventType].forEach((listener: EventListener) => {
              listener.call(document, storedEvent);
            });
          }
        } finally {
          document.elementFromPoint = tempPatch;
        }
      }) as EventListener;

      shadowRoot.addEventListener(eventType, forwarder, true);
      dragForwardListeners.push(() => shadowRoot.removeEventListener(eventType, forwarder, true));
    });

    mouseEvents.forEach(eventType => {
      const forwarder = ((e: MouseEvent) => {
        const storedEvent = e;
        const syntheticEvent = new MouseEvent(eventType, {
          bubbles: true,
          cancelable: true,
          view: window,
          detail: storedEvent.detail,
          screenX: storedEvent.screenX,
          screenY: storedEvent.screenY,
          clientX: storedEvent.clientX,
          clientY: storedEvent.clientY,
          ctrlKey: storedEvent.ctrlKey,
          altKey: storedEvent.altKey,
          shiftKey: storedEvent.shiftKey,
          metaKey: storedEvent.metaKey,
          button: storedEvent.button,
          buttons: storedEvent.buttons,
          relatedTarget: storedEvent.relatedTarget || undefined
        });

        document.dispatchEvent(syntheticEvent);
      }) as EventListener;

      shadowRoot.addEventListener(eventType, forwarder, true);
      mouseForwardListeners.push(() => shadowRoot.removeEventListener(eventType, forwarder, true));
    });

    // Intercept document.addEventListener to track drag listeners
    const originalAddEventListener = document.addEventListener.bind(document);
    const shadowEventListeners: { [key: string]: EventListener[] } = {};
    
    document.addEventListener = function(type: string, listener: any, options?: any) {
      // Track drag listeners
      if (typeof type === 'string' && typeof listener === 'function' && (type.startsWith('drag') || mouseEvents.includes(type))) {
        if (!shadowEventListeners[type]) {
          shadowEventListeners[type] = [];
        }
        if (!shadowEventListeners[type].includes(listener)) {
          shadowEventListeners[type].push(listener);
        }
      }
      
      return originalAddEventListener(type, listener, options);
    } as any;
    
    (document as any).__shadow_event_listeners = shadowEventListeners;

    console.log('[meilook Calendar DnD] Event forwarding setup complete');

    return () => {
      console.log('[meilook Calendar DnD] Restoring original functions');
      document.elementFromPoint = originalElementFromPoint;
      document.elementsFromPoint = originalElementsFromPoint;
      document.addEventListener = originalAddEventListener;
      delete (document as any).__shadow_event_listeners;
      
      // Remove all forward listeners
      dragForwardListeners.forEach(cleanup => cleanup());
      mouseForwardListeners.forEach(cleanup => cleanup());
    };
  }, []);

  // Consolidated ResizeObserver - single source of truth for container width
  // Handles both initial measurement and view changes
  useEffect(() => {
    if (!calendarContainerRef.current) return;

    // Helper to calculate width based on view
    const calculateWidth = (rawWidth: number) => {
      return view === 'week'
        ? Math.floor(rawWidth / 7) - 30  // More conservative for week view
        : Math.floor(rawWidth / 7) - 16; // More conservative for month view
    };

    // Initial measurement
    const rawWidth = calendarContainerRef.current.getBoundingClientRect().width;
    const initialWidth = calculateWidth(rawWidth);
    setContainerWidth(initialWidth);

    // Track previous width to avoid unnecessary updates
    let prevCalculatedWidth = initialWidth;

    // Create ResizeObserver for continuous updates
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const rawWidth = entry.contentRect.width;
        const calculatedWidth = calculateWidth(rawWidth);

        // Only update if width changed significantly (>2px threshold)
        if (Math.abs(calculatedWidth - prevCalculatedWidth) > 2) {
          prevCalculatedWidth = calculatedWidth;
          updateContainerWidth(calculatedWidth);
        }
      }
    });

    // Start observing the container
    resizeObserver.observe(calendarContainerRef.current);

    // Clean up
    return () => {
      resizeObserver.disconnect();
    };
  }, [view, updateContainerWidth]);

  // Listen for changes to the focused date from the thunk
  useEffect(() => {
    if (focusedDate) {
      dispatch(setCurrentDate(focusedDate));
    }
  }, [focusedDate, dispatch]);

  useEffect(() => {
    moment.updateLocale('en', {
      week: {
        dow: startDayAtWeek
      }
    });
  }, [startDayAtWeek]);

  // Clear any pending timeouts on unmount to avoid firing on a stale component
  useEffect(() => {
    return () => {
      pendingTimeoutsRef.current.forEach(clearTimeout);
      pendingTimeoutsRef.current = [];
    };
  }, []);

  // Navigation callback - allows navigation to any date (past or future)
  // No restrictions on date navigation - users can navigate to any month/year
  // Memoized with stable dispatch dependency to prevent unnecessary re-renders
  const onNavigate = useCallback(
    (newDate: Date, view: View, action?: NavigateAction): void => {
      // Accept any date without validation - allows backward navigation to past months
      dispatch(setCurrentDate(newDate));
    },
    [dispatch]
  );

  // Create stable reference to onNavigate for components useMemo
  const stableOnNavigate = onNavigate;

  const onSelectEvent = useCallback((
    event: any,
    e: SyntheticEvent<HTMLElement, Event>
  ): void => {
    const nativeEvent = e.nativeEvent as MouseEvent;
    if (!nativeEvent) return;
    
    if (event.isCampaign) {
      // BE#313 — a DRIP occurrence (the current step OR a synthetic follow-up step) opens the unified
      // SequenceDetail at /campaigns/:id, which renders ALL the drip's steps + per-step edit — exactly
      // like opening it from the campaigns list (a standalone campaign too, by its own id).
      const target = resolveCalendarEditTarget(event as any);
      if (target?.kind === 'sequence-detail') {
        // The same launcher as the v2 grid: in the extension it opens the overlay BEFORE routing it
        // (FE#318) and closes this planner overlay, which would otherwise cover the page.
        navigateToCampaignPage(campaignPagePath(target.campaignId, (event as any).dripStepIndex, 'calendar'), {
          dispatch,
          navigateWeb: (path) => websiteNavigateRef.current?.(path),
        });
        return;
      }
      // Handle campaign slot - open the CampaignRowMenu
      // Store click position for menu positioning
      setMenuPosition({ x: nativeEvent.clientX, y: nativeEvent.clientY });
      setSelectedCampaignId(event._id);
      
      // Position the trigger at the click location
      if (menuTriggerRef.current) {
        menuTriggerRef.current.style.position = 'fixed';
        menuTriggerRef.current.style.left = `${nativeEvent.clientX}px`;
        menuTriggerRef.current.style.top = `${nativeEvent.clientY}px`;
        menuTriggerRef.current.style.width = '2px';
        menuTriggerRef.current.style.height = '2px';
        menuTriggerRef.current.style.opacity = '0.01';
        menuTriggerRef.current.style.pointerEvents = 'auto';
        menuTriggerRef.current.style.zIndex = '10000';
      }
      
      // Use setTimeout to ensure the trigger is positioned and rendered before opening
      pendingTimeoutsRef.current.push(setTimeout(() => {
        if (menuTriggerRef.current) {
          // Force trigger to be clickable
          menuTriggerRef.current.click();
        }
        setIsMenuOpen(true);
      }, 10));
    } else {
      // Handle regular message - open message menu instead of directly opening edit dialog
      // Store click position for menu positioning
      setMessageMenuPosition({ x: nativeEvent.clientX, y: nativeEvent.clientY });
      setSelectedMessageEvent(event);
      
      // Position the trigger at the click location
      if (messageMenuTriggerRef.current) {
        messageMenuTriggerRef.current.style.position = 'fixed';
        messageMenuTriggerRef.current.style.left = `${nativeEvent.clientX}px`;
        messageMenuTriggerRef.current.style.top = `${nativeEvent.clientY}px`;
        messageMenuTriggerRef.current.style.width = '2px';
        messageMenuTriggerRef.current.style.height = '2px';
        messageMenuTriggerRef.current.style.opacity = '0.01';
        messageMenuTriggerRef.current.style.pointerEvents = 'auto';
        messageMenuTriggerRef.current.style.zIndex = '10000';
      }
      
      // Use setTimeout to ensure the trigger is positioned and rendered before opening
      pendingTimeoutsRef.current.push(setTimeout(() => {
        if (messageMenuTriggerRef.current) {
          // Force trigger to be clickable
          messageMenuTriggerRef.current.click();
        }
        setIsMessageMenuOpen(true);
      }, 10));
    }
  }, []);

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    // Set flag to prevent opening new message dialog immediately after closing menu
    menuJustClosedRef.current = true;
    pendingTimeoutsRef.current.push(setTimeout(() => {
      menuJustClosedRef.current = false;
    }, 100));
    setSelectedCampaignId(null);
    setMenuPosition(null);
  };

  const handleCloseMessageMenu = () => {
    setIsMessageMenuOpen(false);
    setSelectedMessageEvent(null);
    setMessageMenuPosition(null);
  };

  const onEventDrop = useCallback(async ({
    event,
    start,
  }: {
    event: any;
    start: stringOrDate;
  }) => {
    // Handle campaigns first - dispatch to campaign thunk
    if (event.isCampaign) {
      const thunkView = view === 'work_week' ? 'week' : view as 'month' | 'week' | 'day';
      const result = await dispatch(onCampaignEventDropThunk({
        campaignId: event._id,
        start,
        view: thunkView
      })).unwrap();

      // Show warning if campaign drag was blocked
      if (!result.success) {
        let message = '';

        switch (result.errorType) {
          case 'not_pending':
            const campaignStatus = result.campaignStatus ?? '';
            const statusDisplay = (campaignStatus.charAt(0).toUpperCase() + campaignStatus.slice(1)) || 'Unknown';
            message = `Cannot move ${statusDisplay.toLowerCase()} campaign. Only pending campaigns can be rescheduled.`;
            break;
          case 'too_close':
            message = `Campaign starts in ${result.minutesUntilDue} minute${result.minutesUntilDue === 1 ? '' : 's'}. Too close to reschedule safely.`;
            break;
          case 'not_found':
            message = 'Campaign not found and cannot be moved.';
            break;
          case 'no_change':
            // No warning needed for no change
            return;
          default:
            message = 'This campaign cannot be moved at this time.';
        }

        setWarningMessage(message);
        setShowWarningDialog(true);
      }
      return;
    }

    // FE#264: on a cross-day move keep the source time-of-day (e.g. 12:12) and only
    // change the date — react-big-calendar's drop `start` otherwise snaps to the
    // day-cell slot. Intra-day drops (week/day time slots) still retime as before.
    const dropDateIso = computeDroppedDueDate(event.dueDate, start);
    console.log('[Calendar DnD] computed ISO for drop', dropDateIso);

    if (event.isRecurring) {
      // Use dedicated thunk that handles data fetching internally
      try {
        const updatedEvent = await dispatch(handleRecurringEventDragThunk({
          leanEvent: event,
          newDueDate: dropDateIso
        })).unwrap();

        setDndEvent(updatedEvent);
        setShowDndConfirmationDialog(true);
      } catch (error) {
        console.error('Error handling recurring event drag:', error);
      }
    } else {
      // Use the dedicated thunk that handles data fetching internally
      dispatch(scheduleMessageFromLeanEventThunk({
        leanEvent: event,
        updates: { dueDate: new Date(dropDateIso) }
      }));
    }
  }, [dispatch, view]);

  const onDndRecurringDialogClose = async (value?: confirmationDialogOption) => {
    if (value && dndEvent) {
      const res = await dispatch(onRecurringDialogCloseThunk({ value, event: dndEvent }));
      // #1136: a recurring move whose replacement was not stored now rejects — say so.
      if (onRecurringDialogCloseThunk.rejected.match(res)) {
        toastDBCreateError(res.error, 'Your change was not saved.');
      }
    } else {
    }

    setShowDndConfirmationDialog(false);
    setDndEvent(null);
  };

  const onView = useCallback((view: View) => {
    dispatch(setView(view));
  }, [dispatch]);

  // const onRightClick = useCallback(async (pl: EventPL) => {
  //   if (!pl.isCampaign) {
  //     try {
  //       // Use the dedicated thunk that handles data fetching internally
  //       const eventWithFullData = await dispatch(handleRightClickFromLeanEventThunk({
  //         leanEvent: pl,
  //         domRect: { left: pl.left, top: pl.top, bottom: pl.bottom, right: pl.right }
  //       })).unwrap();

  //       setEventPL(eventWithFullData);
  //       setShowColorsDialog(true);
  //     } catch (error) {
  //       console.error('Error handling right-click:', error);
  //       // Fallback to using the lean event
  //       setEventPL(pl);
  //       setShowColorsDialog(true);
  //     }
  //   } else {
  //     // inform the user that this is a campaign and its color cannot be changed
  //     setWarningMessage('This is a campaign and its color cannot be changed');
  //     setShowWarningDialog(true);
  //   }
  // }, [dispatch]);

  // OPTIMIZATION: components map identity no longer depends on containerWidth — width flows
  // through CalendarContainerWidthContext, so a small resize doesn't remount every day cell
  const components = useMemo(
    () => ({
      toolbar: (v: ToolbarProps<any, any>) => (
        <CustomToolbar toolbarProps={v} calendarOnNavigate={stableOnNavigate} />
      ),
      month: {
        event: CalendarMonthEvent,
        dateHeader: CalendarDateHeader,
        dateCellWrapper: CalendarDateCellWrapper,
      },
      week: {
        event: CalendarMonthEvent,
        timeSlotWrapper: CalendarTimeSlotWrapper,
      },
      day: {
        event: CalendarMonthEvent,
        timeSlotWrapper: CalendarTimeSlotWrapper,
      },
    }),
    [stableOnNavigate]
  );

  const onWarningDialogClose = () => {
    setShowWarningDialog(false);
    setWarningMessage('');
  };

  // DEBUG: Log render completion (only when DEBUG_PERF is enabled)
  if (DEBUG_PERF) {
    const renderTime = performance.now() - renderStart;
    console.log(`${PERF_PREFIX} Render #${renderCount} setup complete: ${renderTime.toFixed(2)}ms`);

    // Log summary every 5 renders
    if (renderCount % 5 === 0) {
      console.log(`${PERF_PREFIX} ===== SUMMARY after ${renderCount} renders =====`);
      console.log(`${PERF_PREFIX} Current render time: ${renderTime.toFixed(2)}ms`);
      console.log(`${PERF_PREFIX} Events count: ${events?.length || 0}`);
    }
  }

  return (
    <>
      {/* Website only: publish the history router's navigate into the ref.
          Never rendered in the extension overlay (no <Router> → would throw). */}
      {!isExtensionContext() && <WebsiteNavigateBridge navigateRef={websiteNavigateRef} />}
      <BTThemeProvider>
        <Box sx={{
          flex: 1,
          height: '100%',
          transition: 'all 0.2s',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '100%',
          minWidth: 0, // flexbox: don't expand to content min-content width (mobile overflow)
        }}>
          <DialogContent
            sx={{
              flex: 1,
              padding: isMobile ? '0 8px 8px 8px !important' : '0 24px 24px 24px !important',
              boxSizing: 'border-box',
              backgroundColor: 'var(--calendar-bg)',
              position: 'relative',
              height: '100%',
              maxHeight: '100%',
              overflow: 'hidden',
              minWidth: 0,
            }}>
            <>
              {/* Removed unused keyframes style */}
              <div
                ref={setCalendarContainerRef}
                style={{ width: '100%', height: '100%', position: 'relative', minWidth: 0 }}
              >
                <CalendarContainerWidthContext.Provider value={containerWidth}>
                  <Suspense fallback={<CalendarSkeleton />}>
                  <LazyDnDCalendar
                    key={view}
                    culture={i18n.language || 'en'}
                    rtl={isRtl}
                    events={events}
                    titleAccessor={calendarTitleAccessor}
                    startAccessor={calendarEventDateAccessor}
                    endAccessor={calendarEventDateAccessor}
                    date={currentDate}
                    onNavigate={onNavigate}
                    onSelectEvent={onSelectEvent}
                    view={view}
                    onView={onView}
                    resizable={false}
                    selectable={true}
                    onEventDrop={onEventDrop}
                    components={components}
                    dayLayoutAlgorithm="no-overlap"
                    draggableAccessor={calendarDraggableAccessor}
                    scrollToTime={calendarScrollToTime}
                    formats={calendarFormats}
                    step={30}
                    timeslots={1}
                    min={calendarMinTime}
                    max={calendarMaxTime}
                    showMultiDayTimes={true}
                    longPressThreshold={100}
                    popup={true}
                    style={calendarStyle}
                  />
                  </Suspense>
                </CalendarContainerWidthContext.Provider>
              </div>
            </>
          </DialogContent>
        </Box>
        {showDndConfirmationDialog && (
          <MessageConfirmationDialog
            title="Edit recurring message"
            options={confirmationDialogOptionsLocal.slice(0, 2)}
            onClose={onDndRecurringDialogClose}
          />
        )}
        {showWarningDialog && (
          <MessageConfirmationDialog
            title={warningMessage}
            onClose={onWarningDialogClose}
            shouldShowCancelButton={false}
          />
        )}
        {selectedCampaignId && menuPosition && (
          <DropdownMenu open={isMenuOpen} onOpenChange={(open) => {
            if (!open) {
              handleCloseMenu();
            } else {
              setIsMenuOpen(true);
            }
          }}>
            <DropdownMenuTrigger asChild>
              <button
                ref={menuTriggerRef}
                style={{
                  position: 'fixed',
                  left: menuPosition ? `${menuPosition.x}px` : '0px',
                  top: menuPosition ? `${menuPosition.y}px` : '0px',
                  width: '4px',
                  height: '4px',
                  opacity: 0.01,
                  pointerEvents: 'auto',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  margin: 0,
                  zIndex: 10000,
                  cursor: 'pointer',
                }}
                aria-hidden="true"
                tabIndex={-1}
              />
            </DropdownMenuTrigger>
            <CampaignRowMenu
              campaignId={selectedCampaignId}
              shouldShowViewLogs={true}
              isCampaignDuplicateShow={true}
              isCampaignSummaryShow={true}
              fromCalendar={true}
              onMenuClose={() => setIsMenuOpen(false)}
            />
          </DropdownMenu>
        )}
        {/* Message menu - תפריט להודעות רגילות */}
        {selectedMessageEvent && messageMenuPosition && (
          <DropdownMenu open={isMessageMenuOpen} onOpenChange={(open) => {
            if (!open) {
              handleCloseMessageMenu();
            } else {
              setIsMessageMenuOpen(true);
            }
          }}>
            <DropdownMenuTrigger asChild>
              <button
                ref={messageMenuTriggerRef}
                style={{
                  position: 'fixed',
                  left: messageMenuPosition ? `${messageMenuPosition.x}px` : '0px',
                  top: messageMenuPosition ? `${messageMenuPosition.y}px` : '0px',
                  width: '4px',
                  height: '4px',
                  opacity: 0.01,
                  pointerEvents: 'auto',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  margin: 0,
                  zIndex: 10000,
                  cursor: 'pointer',
                }}
                aria-hidden="true"
                tabIndex={-1}
              />
            </DropdownMenuTrigger>
            <MessageRowMenu
              message={selectedMessageEvent}
              onMenuClose={() => setIsMessageMenuOpen(false)}
            />
          </DropdownMenu>
        )}
      </BTThemeProvider>
      {/* ScheduledMessageEditorPopup is now handled by separate injection (scheduled-message-dialog.injection.tsx) */}
      {/* It's triggered via Redux state and renders in its own shadow DOM with proper z-index */}
      {/* {isAddScheduledMessageModalOpen && <ScheduledMessageEditorPopup />} */}

      <NewMessageFab />
    </>
  );
}

export default CalendarView;
